/**
 * Form Designer — Access-style regions:
 *   LEFT  = database fields from the primary table
 *   TOP   = control toolbox (Label, Text Box, Button, Tab, …)
 *   CENTER = grid canvas
 *   RIGHT = properties of the selected object
 *
 * This component owns the whole form document (name, primary table, cards,
 * controls) and the current selection (selectedForm / selectedCard /
 * selectedControl). Database columns and FHIR semantic fields coexist: a
 * control may bind to a catalog column, a FHIR path, or both. The command
 * bar is the primary command interface.
 *
 * A new form has no primary table. The first table in the catalog is never
 * assumed, so the field list stays empty and Save stays disabled until a
 * primary table is chosen.
 *
 * Either side panel can be collapsed to a rail. Collapsing is presentation
 * only: the form and the current selection live here, so nothing about the
 * document or the selection changes when a panel opens or closes — the canvas
 * simply gets the width back.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Database,
  FolderOpen,
  SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FD } from "@/constants/testIds";
import {
  NO_CARD_MESSAGE,
  NO_ROOM_MESSAGE,
  addFieldControl,
  addStaticControl,
  alignCardsBottom,
  alignCardsLeft,
  alignCardsRight,
  alignCardsTop,
  emptyForm,
  findCard,
  findControl,
  moveCard,
  moveCardTo,
  moveCardsTo,
  moveControl,
  normalizeForm,
  nudgeCards,
  reassignControl,
  removeCard,
  resizeCardTo,
  resizeControl,
  toSavePayload,
  updateCard,
  updateControl,
} from "@/lib/formDesigner";
import {
  runAddCard,
  runAddControl,
  runDeleteCard,
  runDeleteControl,
  runNewForm,
  selectControl,
  selectForm,
} from "@/lib/formDesignerCommands";
import { describeField } from "@/lib/formDesignerFields";
import { fieldFromSemantic, findSemanticField } from "@/lib/formDesignerSemantic";
import {
  deleteFormDesign,
  getBindableFields,
  getFormDesign,
  listFormDesigns,
  saveFormDesign,
} from "@/lib/formDesignerApi";
import FormDesignerCanvas from "./FormDesignerCanvas";
import FormDesignerCommandBar from "./FormDesignerCommandBar";
import FormDesignerPalette from "./FormDesignerPalette";
import FormDesignerProperties from "./FormDesignerProperties";
import FormDesignerToolbox from "./FormDesignerToolbox";
import "@/styles/emr-form-designer.css";

function snapshotOf(form) {
  return JSON.stringify(toSavePayload(form));
}

function errorText(error, fallback) {
  return error?.response?.data?.detail || fallback;
}

export default function FormDesigner({ tables = [], canWrite = false }) {
  const [form, setForm] = useState(() => emptyForm());
  const [selection, setSelection] = useState(null);
  const [fields, setFields] = useState([]);
  const [fieldsStatus, setFieldsStatus] = useState("idle");
  const [savedForms, setSavedForms] = useState([]);
  const [openId, setOpenId] = useState("");
  const [busy, setBusy] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(true);
  const [propsOpen, setPropsOpen] = useState(true);
  const [baseline, setBaseline] = useState(() => snapshotOf(emptyForm()));
  const [pendingDelete, setPendingDelete] = useState(null);

  const readOnly = !canWrite;
  const dirty = snapshotOf(form) !== baseline;

  const refreshSavedForms = useCallback(
    () =>
      listFormDesigns()
        .then((payload) => setSavedForms(payload.items || []))
        .catch(() => setSavedForms([])),
    [],
  );

  useEffect(() => {
    refreshSavedForms();
  }, [refreshSavedForms]);

  /** One table's fields, and only once a primary table exists. */
  useEffect(() => {
    if (!form.primaryTable) {
      setFields([]);
      setFieldsStatus("idle");
      return undefined;
    }
    let cancelled = false;
    setFieldsStatus("loading");
    getBindableFields(form.primarySchema || "public", form.primaryTable)
      .then((payload) => {
        if (cancelled) return;
        setFields(payload.fields || []);
        setFieldsStatus("ready");
      })
      .catch((error) => {
        if (cancelled) return;
        setFields([]);
        setFieldsStatus("error");
        toast.error(errorText(error, `Could not load fields for ${form.primaryTable}`));
      });
    return () => {
      cancelled = true;
    };
  }, [form.primarySchema, form.primaryTable]);

  /** Where a palette item lands when it is clicked rather than dragged. */
  const targetCardId = useMemo(() => {
    if (selection?.kind === "card") return selection.id;
    if (selection?.kind === "control") return findControl(form, selection.id)?.cardId || null;
    const cards = form.cards || [];
    return cards.length ? cards[cards.length - 1].id : null;
  }, [form, selection]);

  function apply(next, message) {
    if (!next) {
      if (message) toast.error(message);
      return false;
    }
    setForm(next);
    return true;
  }

  function handlePrimaryTable(name) {
    const match = tables.find((item) => item.name === name);
    setSelection(null);
    // Bindings belong to the old table. Carrying cards across would leave
    // controls pointing at columns that may not exist on the new one.
    setForm((current) => ({
      ...current,
      primaryTable: name,
      primarySchema: match?.schema || "public",
      cards: [],
      controls: [],
    }));
  }

  function handleAddCard() {
    const result = runAddCard(form);
    if (!result) {
      toast.error("No room left on the form canvas. Move or resize a card.");
      return;
    }
    setForm(result.form);
    setSelection(result.selection);
  }

  function handleAddControl() {
    const cardId = selection?.kind === "card" ? selection.id : null;
    if (!cardId) {
      toast.error(NO_CARD_MESSAGE);
      return;
    }
    const result = runAddControl(form, cardId);
    if (!result) {
      toast.error(NO_ROOM_MESSAGE);
      return;
    }
    setForm(result.form);
    setSelection(result.selection);
  }

  function handleSelect(next) {
    if (!next) {
      setSelection(null);
      return;
    }
    if (next.kind === "card" && next.additive) {
      const currentIds =
        selection?.kind === "card" ? selection.ids || (selection.id ? [selection.id] : []) : [];
      const exists = currentIds.includes(next.id);
      const ids = exists ? currentIds.filter((id) => id !== next.id) : [...currentIds, next.id];
      if (ids.length === 0) {
        setSelection(null);
        return;
      }
      setSelection({ kind: "card", id: ids[ids.length - 1], ids });
      return;
    }
    if (next.kind === "card") {
      setSelection({ kind: "card", id: next.id, ids: next.ids || [next.id] });
      return;
    }
    setSelection(next);
  }

  function placeFromPalette(cardId, item, at) {
    if (!cardId) {
      toast.error(NO_CARD_MESSAGE);
      return;
    }
    let result = null;
    if (item.kind === "semantic") {
      const semantic = findSemanticField(item.id);
      if (!semantic) return;
      result = addFieldControl(form, cardId, fieldFromSemantic(semantic), at);
    } else if (item.kind === "field") {
      const field = fields.find((entry) => entry.name === item.name);
      if (!field) return;
      // The control is captioned the way the Fields panel named the field, and
      // still binds to the column itself.
      result = addFieldControl(
        form,
        cardId,
        { ...field, displayLabel: describeField(field).displayLabel },
        at,
      );
    } else {
      result = addStaticControl(form, cardId, item.controlType, at);
    }
    if (!result) {
      toast.error(NO_ROOM_MESSAGE);
      return;
    }
    setForm(result.form);
    setSelection(selectControl(result.control.id));
  }

  const handleMoveControl = useCallback((id, col, row) => {
    setForm((current) => moveControl(current, id, col, row) || current);
  }, []);

  const handleResizeControl = useCallback((id, width, height) => {
    setForm((current) => resizeControl(current, id, width, height) || current);
  }, []);

  const handleMoveCard = useCallback((id, col, row) => {
    setForm((current) => moveCardTo(current, id, col, row) || current);
  }, []);

  const handleMoveCards = useCallback((moves) => {
    setForm((current) => moveCardsTo(current, moves) || current);
  }, []);

  const handleResizeCard = useCallback((id, width, height) => {
    setForm((current) => resizeCardTo(current, id, width, height) || current);
  }, []);

  const handleNudgeCards = useCallback((ids, dCol, dRow) => {
    setForm((current) => nudgeCards(current, ids, dCol, dRow) || current);
  }, []);

  const selectedCardIds = selection?.kind === "card" ? selection.ids || (selection.id ? [selection.id] : []) : [];

  function handleAlign(align) {
    const next = align(form, selectedCardIds);
    if (!next) {
      toast.error("Can't align: the result would overlap another card or leave the grid.");
      return;
    }
    setForm(next);
  }

  function handleDeleteCard(id) {
    if (readOnly) return;
    const result = runDeleteCard(form, id);
    if (!result) return;
    setForm(result.form);
    if (selectedCardIds.includes(id)) setSelection(result.selection);
  }

  /** The command-bar Delete Card removes the selected card, never a control. */
  function handleDeleteSelectedCards() {
    if (readOnly || selection?.kind !== "card" || selectedCardIds.length === 0) return;
    const next = selectedCardIds.reduce((carry, id) => removeCard(carry, id) || carry, form);
    if (next === form) return;
    setForm(next);
    setSelection(selectForm());
  }

  function handleDeleteSelectedControl() {
    if (readOnly || selection?.kind !== "control") return;
    const result = runDeleteControl(form, selection.id);
    if (!result) return;
    setForm(result.form);
    setSelection(result.selection);
  }

  const DELETE_COPY = {
    form: {
      title: "Delete this form?",
      description: "The form definition will be removed. Patient data and database tables are not touched.",
    },
    card: {
      title: "Delete this card?",
      description: "The card and the controls on it will be removed from the form.",
    },
    control: {
      title: "Delete this control?",
      description: "The selected control will be removed from its card. The card stays.",
    },
  };

  function requestDelete(level, id) {
    if (readOnly) return;
    setPendingDelete({ level, id });
  }

  function confirmPendingDelete() {
    const pending = pendingDelete;
    setPendingDelete(null);
    if (!pending) return;
    if (pending.level === "form") {
      handleDeleteForm();
      return;
    }
    if (pending.level === "card") {
      if (pending.id) handleDeleteCard(pending.id);
      else handleDeleteSelectedCards();
      return;
    }
    if (pending.level === "control") {
      if (pending.id) {
        const result = runDeleteControl(form, pending.id);
        if (result) {
          setForm(result.form);
          setSelection(result.selection);
        }
        return;
      }
      handleDeleteSelectedControl();
    }
  }

  function handleControlChange(id, patch, mode) {
    if (mode === "resize") {
      const control = findControl(form, id);
      if (!control) return;
      const width = patch.width ?? control.width;
      const height = patch.height ?? control.height;
      apply(resizeControl(form, id, width, height), "That size does not fit in this card");
      return;
    }
    if (mode === "move") {
      const control = findControl(form, id);
      if (!control) return;
      apply(moveControl(form, id, patch.col ?? control.col, patch.row ?? control.row), "That position does not fit in this card");
      return;
    }
    apply(updateControl(form, id, patch));
  }

  function handleNewForm() {
    const next = runNewForm();
    setForm(next.form);
    setSelection(next.selection);
    setOpenId("");
    setFields([]);
    setFieldsStatus("idle");
    setBaseline(snapshotOf(next.form));
  }

  async function handleDeleteForm() {
    if (readOnly) return;
    const id = form.id || openId;
    if (id) {
      try {
        setBusy(true);
        await deleteFormDesign(id);
        await refreshSavedForms();
      } catch (error) {
        toast.error(errorText(error, "Could not delete the form"));
        setBusy(false);
        return;
      } finally {
        setBusy(false);
      }
    }
    handleNewForm();
    toast.success("Form deleted");
  }

  async function handleOpenForm() {
    if (!openId) return;
    try {
      setBusy(true);
      const payload = await getFormDesign(openId);
      const saved = payload.form || {};
      setFields(saved.primaryTableFields || []);
      setFieldsStatus(saved.primaryTable ? "ready" : "idle");
      const opened = normalizeForm(saved, (saved.primaryTableFields || []).map((field) => field.name));
      setForm(opened);
      setSelection(selectForm());
      setBaseline(snapshotOf(opened));
      toast.success(`Opened ${saved.name}`);
    } catch (error) {
      toast.error(errorText(error, "Could not open that form"));
    } finally {
      setBusy(false);
    }
  }

  async function handleSave() {
    if (readOnly || !form.primaryTable) return;
    try {
      setBusy(true);
      const payload = await saveFormDesign(toSavePayload(form));
      const saved = payload.form || {};
      const withId = { ...form, id: saved.id };
      setForm(withId);
      setOpenId(saved.id || "");
      setBaseline(snapshotOf(withId));
      await refreshSavedForms();
      toast.success(`Saved ${saved.name || form.name}`);
    } catch (error) {
      toast.error(errorText(error, "Could not save the form"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="efd" data-testid={FD.page}>
      {/* One compact application-style band: the title, the four pieces of form
          metadata, then the actions. Card actions are grouped apart from the
          document actions so + and 🗑 read as a pair. */}
      <div className="efd__toolbar">
        <h2 className="efd__title" data-testid={FD.title}>
          Form Designer
        </h2>
        <div className="efd__field efd__field--inline">
          <label htmlFor="efd-form-name">Form Name</label>
          <input
            id="efd-form-name"
            data-testid={FD.name}
            value={form.name}
            disabled={readOnly}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
          />
        </div>
        <div className="efd__field efd__field--inline">
          <label htmlFor="efd-primary-table">Primary Table</label>
          <select
            id="efd-primary-table"
            data-testid={FD.primaryTable}
            value={form.primaryTable}
            disabled={readOnly}
            onChange={(event) => handlePrimaryTable(event.target.value)}
          >
            <option value="">Select a table</option>
            {tables.map((item) => (
              <option key={`${item.schema}.${item.name}`} value={item.name}>
                {item.name}
              </option>
            ))}
          </select>
        </div>
        <div className="efd__field efd__field--inline efd__field--grow">
          <label htmlFor="efd-description">Description</label>
          <input
            id="efd-description"
            data-testid={FD.description}
            value={form.description}
            disabled={readOnly}
            placeholder="What this form is for"
            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
          />
        </div>
        <div className="efd__field efd__field--inline">
          <label htmlFor="efd-saved-forms">Saved Forms</label>
          <select
            id="efd-saved-forms"
            data-testid={FD.savedForms}
            value={openId}
            onChange={(event) => setOpenId(event.target.value)}
          >
            <option value="">Select a form</option>
            {savedForms.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} · {item.primaryTable}
              </option>
            ))}
          </select>
        </div>
        <div className="efd__toolbar-actions">
          <button
            type="button"
            className="efd__tool-btn"
            data-testid={FD.openFormBtn}
            aria-label="Open Form"
            title="Open Form"
            disabled={!openId || busy}
            onClick={handleOpenForm}
          >
            <FolderOpen className="efd__icon" aria-hidden="true" />
          </button>
        </div>
      </div>

      <FormDesignerCommandBar
        form={form}
        selection={selection}
        readOnly={readOnly}
        dirty={dirty}
        busy={busy}
        onNewForm={handleNewForm}
        onDeleteForm={() => requestDelete("form")}
        onAddCard={handleAddCard}
        onDeleteCard={() => requestDelete("card")}
        onAddControl={handleAddControl}
        onDeleteControl={() => requestDelete("control")}
        onSave={handleSave}
      />

      <FormDesignerToolbox
        readOnly={readOnly}
        onAddControl={(controlType) => placeFromPalette(targetCardId, { kind: "control", controlType }, null)}
      />

      <div
        className={`efd__workspace${fieldsOpen ? "" : " efd__workspace--no-left"}${propsOpen ? "" : " efd__workspace--no-right"}`}
        data-testid={FD.workspace}
        data-fields-panel={fieldsOpen ? "open" : "collapsed"}
        data-properties-panel={propsOpen ? "open" : "collapsed"}
      >
        {fieldsOpen ? (
          <FormDesignerPalette
            primaryTable={form.primaryTable}
            fields={fields}
            status={fieldsStatus}
            form={form}
            readOnly={readOnly}
            onCollapse={() => setFieldsOpen(false)}
            onAddField={(field) => placeFromPalette(targetCardId, { kind: "field", name: field.name }, null)}
            onAddSemantic={(field) => placeFromPalette(targetCardId, { kind: "semantic", id: field.id }, null)}
          />
        ) : (
          <aside className="efd__rail" data-testid={FD.paletteRail}>
            <button
              type="button"
              className="efd__icon-btn"
              data-testid={FD.paletteExpand}
              aria-label="Expand Database Fields"
              title="Expand Database Fields"
              onClick={() => setFieldsOpen(true)}
            >
              <ChevronRight className="efd__icon" aria-hidden="true" />
            </button>
            <span className="efd__rail-label">
              <Database className="efd__icon" aria-hidden="true" />
              Database Fields
            </span>
          </aside>
        )}

        <FormDesignerCanvas
          form={form}
          selection={selection}
          selectedCardIds={selectedCardIds}
          readOnly={readOnly}
          onSelect={handleSelect}
          onAddCard={handleAddCard}
          onMoveControl={handleMoveControl}
          onResizeControl={handleResizeControl}
          onMoveCard={handleMoveCard}
          onMoveCards={handleMoveCards}
          onResizeCard={handleResizeCard}
          onNudgeCards={handleNudgeCards}
          onDeleteCard={(id) => requestDelete("card", id)}
          onDropItem={placeFromPalette}
        />

        {propsOpen ? null : (
          <aside className="efd__rail" data-testid={FD.propsRail}>
            <button
              type="button"
              className="efd__icon-btn"
              data-testid={FD.propsExpand}
              aria-label="Expand Properties"
              title="Expand Properties"
              onClick={() => setPropsOpen(true)}
            >
              <ChevronLeft className="efd__icon" aria-hidden="true" />
            </button>
            <span className="efd__rail-label">
              <SlidersHorizontal className="efd__icon" aria-hidden="true" />
              Properties
            </span>
          </aside>
        )}

        {propsOpen ? (
          <FormDesignerProperties
            form={form}
            fields={fields}
            selection={selection}
            selectedCardIds={selectedCardIds}
            readOnly={readOnly}
            onCollapse={() => setPropsOpen(false)}
            onCardChange={(id, patch) =>
              apply(updateCard(form, id, patch), "That size or position does not fit on the canvas")
            }
            onControlChange={handleControlChange}
            onMoveCard={(id, delta) => apply(moveCard(form, id, delta))}
            onAlign={handleAlign}
            onResizeCard={(id, width, height) =>
              apply(resizeCardTo(form, id, width, height), "That size does not fit on the canvas")
            }
            onDeleteCard={(id) => requestDelete("card", id)}
            onDeleteControl={(id) => requestDelete("control", id)}
            onReassignControl={(id, cardId) =>
              apply(reassignControl(form, id, cardId), `${findCard(form, cardId)?.title || "That card"} has no room left`)
            }
          />
        ) : null}
      </div>

      <AlertDialog open={Boolean(pendingDelete)} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent data-testid={FD.confirmDialog} className="efd__confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingDelete ? DELETE_COPY[pendingDelete.level].title : "Delete?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete ? DELETE_COPY[pendingDelete.level].description : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={FD.confirmCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid={FD.confirmDelete}
              className="efd__btn efd__btn--danger"
              onClick={confirmPendingDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
